import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { Check, Upload } from './icons';
import { VignetteCv } from './VignetteCv';
import { colors, formes, gradients, spacing } from '../theme/tokens';

export interface CarteDepotCvProps {
  // Un CV est choisi : la pastille verte apparaît sur la feuille.
  pret: boolean;
  titre: string;
  // Consigne tant qu'il n'y a rien, nom du fichier ensuite.
  sousTitre: string;
  bouton: string;
  onPress: () => void;
  testID?: string;
  boutonTestID?: string;
  sousTitreTestID?: string;
}

/**
 * Carte de dépôt du CV, en tête de « Note ton CV ». Une feuille schématique penchée à gauche, le
 * titre et le bouton à droite : « Dépose ton CV » tant que rien n'est choisi, « CV prêt à être noté »
 * ensuite. La feuille est immobile ; la maquette y fait passer un balayage, qui viendra avec l'option
 * « réduire les animations ».
 */
export function CarteDepotCv({ pret, titre, sousTitre, bouton, onPress, testID, boutonTestID, sousTitreTestID }: CarteDepotCvProps) {
  return (
    <LinearGradient
      testID={testID}
      colors={gradients.nuitCv}
      start={{ x: 0, y: 0 }}
      end={{ x: 0.4, y: 1 }}
      style={styles.carte}
    >
      <View
        testID={testID ? `${testID}-feuille` : undefined}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={styles.feuilleZone}
      >
        <View style={styles.feuille}>
          <VignetteCv template="accent" accentColor={colors.electricDeep} taille="lg" />
        </View>
        {pret ? (
          <View testID={testID ? `${testID}-pret` : undefined} style={styles.pastille}>
            <Check size={16} color={colors.white} strokeWidth={3} />
          </View>
        ) : null}
      </View>
      <View style={styles.textes}>
        <Text style={styles.titre}>{titre}</Text>
        <Text testID={sousTitreTestID} style={styles.sousTitre} numberOfLines={2}>
          {sousTitre}
        </Text>
        <Pressable
          testID={boutonTestID}
          accessibilityRole="button"
          onPress={onPress}
          style={styles.bouton}
        >
          <Upload size={16} color={colors.night} strokeWidth={2.4} />
          <Text style={styles.boutonTexte}>{bouton}</Text>
        </Pressable>
      </View>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  carte: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg + 2,
    padding: spacing.xl,
    borderRadius: formes.rayon.lg,
    overflow: 'hidden',
  },
  feuilleZone: { width: 92, height: 116, alignItems: 'center', justifyContent: 'center' },
  feuille: { transform: [{ rotate: '-4deg' }], shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 14, shadowOffset: { width: 0, height: 8 } },
  pastille: {
    position: 'absolute',
    right: -2,
    bottom: 0,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.success,
    borderWidth: 3,
    borderColor: colors.night,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textes: { flex: 1, gap: spacing.sm - 2 },
  titre: { fontSize: 19, lineHeight: 23, fontWeight: '800', color: colors.white },
  sousTitre: { fontSize: 12.5, lineHeight: 17, color: colors.blancAtenue },
  bouton: {
    alignSelf: 'flex-start',
    minHeight: formes.cible,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.lg,
    borderRadius: 22,
    backgroundColor: colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm - 1,
  },
  boutonTexte: { fontSize: 13.5, fontWeight: '800', color: colors.night },
});
